import Color = require("color-js");
import thunkMiddleware from "redux-thunk";
import { CoughMarshalClient } from "@vitalograph/coughmarshalclient";
import * as Actions from "./actions";
import rootReducer from "./CoreReducer";
import { Dispatch } from "react-redux";
import { toast } from "react-toastify";
import { Store } from "redux";
import { StudyCoughEventType, CoughAnalysisSetsFixedCommentsDisplay } from "../../misc/api/apiTypes";
import { timerMiddleware } from "../../misc/middleware/TimerMiddleware";
import Productionize from "../../misc/production-middleware";
import { mapToServerEvent } from "./eventMapping";
import { AnalyseState, EditModeEnum, LoadingStateEnum, PlayStateEnum } from "./interfaces/state";
import { PeaksJSChannel, PeaksPeriod, PeaksPoint } from "./interfacesPeaks/PeaksJS";
import { WebPortalLegacyClientInternal } from "../../misc/api/WebPortalTokenClient";
import { MapPeriod } from "./MappingPositionHelper";

export interface ServerEvent {
    AnalysisSetId: string;
    EventTypeId: string;
    RawStart?: number;
    RawEnd?: number;
    ProcessedStart?: number;
    ProcessedEnd?: number;
    SensorProcessedStart?: number;
    SensorProcessedEnd?: number;
}

export enum AnalysisMode {
    Analysis = "Analysis",
    Readonly = "Readonly"
}

export const DEFAULT_WAVEFORM_TYPE = "Raw";

export const initialState = (primarySetId: string, mode?: boolean): AnalyseState => {
    const previousPlaybackTimepoint = localStorage.getItem(
        `analysisPreviousPlaybackTimepoint_${primarySetId}`
    );
    const timepointNumber =
        previousPlaybackTimepoint !== null ? parseFloat(previousPlaybackTimepoint) : null;

    const scrollAction = localStorage.getItem("analysisScrollAction") === "true";

    return {
        core: {
            areKeyboardShortcutsVisible: false,
            availableEventTypes: [],
            availableFixedComments: [],
            currentTimepoint: 0,
            isAddingSessionNote: false,
            isDownloadClicked: false,
            isEditMode: EditModeEnum.NotEditing,
            isEventListOpen: false,
            isFixedCommentsDialogOpen: false,
            isGridlineVisible: true,
            isLoadError: false,
            configErrorMessage: null,
            isMicrophoneMuted: false,
            isScrollActionZoom: scrollAction,
            isSensorMuted: false,
            isSessionNotesVisible: false,
            isViewOnly: mode,
            canAddEventByDoubleClick: mode === false ? true : false,
            loadingState: LoadingStateEnum.Initial,
            mode: mode ? AnalysisMode.Readonly : AnalysisMode.Analysis,
            peaksJs: null,
            playState: PlayStateEnum.NotStarted,
            storedPlaybackTime: timepointNumber,
            previouslySelectedWaveform: null,
            selectedWaveform: DEFAULT_WAVEFORM_TYPE,
            sessionMetadata: null,
            sessionNotes: [],
            studyId: null,
            zoomLevel: 0,
            isHourSegmentsModalOpened: false,
            hourSegmentsMap: new Map(),
            isAcceptabilityAuditModalVisible: false,
            acceptabilityStatus: null,
            isSubmitting: false,
            rawTimestampPoints: null,
            previousPlaybackTimepoint: null,
            playbackServiceStatus: null,
            positions: null
        },
        analysisSet: {
            coughAnalysisSets: new Array<CoughAnalysisSetsFixedCommentsDisplay>(),
            isAnalysisSetDialogOpen: false,
            isLoaded: false,
            isMaximumAnalysisSetsAlertVisible: false,
            primaryAnalystSetId: primarySetId,
        }
    };
};

export default function configureStore(preloadedState: AnalyseState) {
    const createStoreWithMiddleware = Productionize([
        thunkMiddleware,
        peaksJSMiddleware,
        timerMiddleware
    ]);

    return createStoreWithMiddleware(rootReducer, preloadedState) as Store<AnalyseState>;
}

/**
 *
 * Verifying that the tag being added is within the bounds of the recording.
 * 
 * @param state
 * @param timePoint
 */
function VerifyTimeInsideWaveform(state: AnalyseState, timePoint: number): string {
    if ((timePoint === null || timePoint === undefined) || timePoint < 0) {
        return "Invalid Event. Events can't be added outside the cough file.";
    }

    const duration = state.core.peaksJs.player.getDuration();

    return timePoint > duration ? "Invalid Event. Events can't be added outside the cough file." : null;
}

function validateSegment(state: AnalyseState, segment: PeaksPeriod): string {
    return VerifyTimeInsideWaveform(state, segment.startTime) || VerifyTimeInsideWaveform(state, segment.endTime);
}

function validatePoint(state: AnalyseState, point: PeaksPoint): string {
    return VerifyTimeInsideWaveform(state, point.timestamp);
}

function validatePointTimestamps(state: AnalyseState, point: PeaksPoint | PeaksPeriod): string {

    if (point.isPoint) {
        return validatePoint(state, point as PeaksPoint);
    } else {
        return validateSegment(state, point as PeaksPeriod);
    }

}

function peaksJSMiddleware(store: Store<AnalyseState>) {
    return (next: any) => async (action: Actions.Action) => {
        const state = store.getState();

        switch (action.type) {
            case Actions.ActionType.InitializePeaksEvents:
                registerPeaksEvents(store.dispatch, state);
                break;
            case Actions.ActionType.DestoryPeaks:
                if (state.core.peaksJs) {
                    state.core.peaksJs.destroy();
                }
                break;
            case Actions.ActionType.Play:
                state.core.peaksJs.player.play();
                break;
            case Actions.ActionType.Pause:
                state.core.peaksJs.player.pause();
                break;
            case Actions.ActionType.Reset:
                state.core.peaksJs.player.seekBySeconds(0);
                break;
            case Actions.ActionType.ToggleMuteMicrophone:
                state.core.peaksJs.player.toggleChannelMute(PeaksJSChannel.Microphone);
                break;
            case Actions.ActionType.ToggleMuteSensor:
                state.core.peaksJs.player.toggleChannelMute(PeaksJSChannel.Sensor);
                break;
            case Actions.ActionType.ToggleGridline:
                state.core.peaksJs.waveform.toggleGridline();
                break;
            case Actions.ActionType.SetZoomLevel:
                state.core.peaksJs.zoom.setZoom(action.level);
                break;
            case Actions.ActionType.AddEvent:
                if (state.core.playState === PlayStateEnum.Playing) {
                    return;
                }

                const primarySetId = state.analysisSet.primaryAnalystSetId;
                const eventType = state.core.selectedEventType;

                const set = state.analysisSet.coughAnalysisSets.find(i => i.Id === primarySetId);

                let pointEvent: PeaksPoint;
                let periodEvent: PeaksPeriod;
                if (!eventType.IsSegmented) {
                    pointEvent = {
                        ...action.event,
                        labelText: set.Type.Name,
                        isPoint: true,
                        color: Color(eventType.Colour)
                            .setAlpha(0.6)
                            .toCSS(),
                        eventTypeId: eventType.Id,
                        eventTypeName: eventType.EventName,
                        layerPosition: 0
                    } as PeaksPoint;
                }
                else {
                    periodEvent = {
                        id: action.event.id,
                        isPoint: false,
                        labelText: set.Type.Name,
                        editable: action.event.editable,
                        editMode: action.event.editMode,
                        color: Color(eventType.Colour)
                            .setAlpha(0.6)
                            .toCSS(),
                        startTime: action.event.startTime ? action.event.startTime : action.event.timestamp,
                        //Setting the default end time to be a fixed number to allow searching the database for period events
                        //that were created but never edited.
                        endTime: action.event.endTime ? action.event.endTime : action.event.timestamp + 3.141,
                        eventTypeId: eventType.Id,
                        eventTypeName: eventType.EventName,
                        layerPosition: 0
                    } as PeaksPeriod;
                }

                // If there is already a point of the SAME TYPE, then don't add it again and inform the user.
                if (
                    state.core.peaksJs.pointOrSegmentExists(
                        eventType.IsSegmented ? periodEvent : pointEvent,
                        !eventType.IsSegmented
                    )
                ) {
                    toast.error(
                        "A Marker Of Type " + eventType.EventName + " Already Exists At This Point."
                    );
                    return;
                }

                let err = validatePointTimestamps(state, eventType.IsSegmented ? periodEvent : pointEvent);

                if (err) {
                    toast.error(
                        err
                    );
                    return;
                }

                addEventFetch(state, eventType.IsSegmented ? periodEvent : pointEvent, action.coughMarshalClient, action.legacyClient);
                break;
            case Actions.ActionType.SkipBackward:
                state.core.peaksJs.time.setCurrentTime(
                    state.core.peaksJs.time.getCurrentTime() -
                    3.5 * Math.pow(2, state.core.peaksJs.currentZoomLevel)
                );
                break;
            case Actions.ActionType.SkipForward:
                state.core.peaksJs.time.setCurrentTime(
                    state.core.peaksJs.time.getCurrentTime() +
                    3.5 * Math.pow(2, state.core.peaksJs.currentZoomLevel)
                );
                break;
            case Actions.ActionType.MovePointEvent:
                const moveablePointEvent = {
                    ...action.event,
                    editMode: true
                };

                state.core.peaksJs.points.edit(moveablePointEvent);
                break;
            case Actions.ActionType.MovePeriodEvent:
                const moveablePeriodEvent = {
                    ...action.event,
                    editMode: true,
                    editable: true
                };

                state.core.peaksJs.segments.edit(moveablePeriodEvent);
                break;
            case Actions.ActionType.FinishMovePointEvent:
                finishMovePointEvent(state, store.dispatch, action.coughMarshalClient, action.legacyClient);
                break;
            case Actions.ActionType.FinishMovePeriodEvent:
                finishMovePeriodEvent(state, store.dispatch, action.coughMarshalClient, action.legacyClient);
                break;
            case Actions.ActionType.RemovePointEvent:
                let point: PeaksPoint;
                if (action.id) {
                    point = state.core.peaksJs.waveform.points.getPoints({
                        id: action.id
                    })[0];
                } else {
                    point = state.core.peaksJs.waveform.activePoint;
                }
                await removeEventFetch(state, point, action.legacyClient);
                break;
            case Actions.ActionType.RemovePeriodEvent:
                let period: PeaksPeriod;
                if (action.id) {
                    period = state.core.peaksJs.waveform.segments.getSegments({
                        id: action.id
                    })[0];
                } else {
                    period = state.core.peaksJs.waveform.activeSegment;
                }
                await removeEventFetch(state, period, action.legacyClient);
                break;
            case Actions.ActionType.ResumePlaybackJump:
                state.core.peaksJs.player.seekBySeconds(state.core.storedPlaybackTime);
                break;
            case Actions.ActionType.SavePlaybackPosition:
                localStorage.setItem(
                    `analysisPreviousPlaybackTimepoint_${state.analysisSet.primaryAnalystSetId}`,
                    state.core.peaksJs.time.getCurrentTime().toString()
                );
                break;
            case Actions.ActionType.ToggleScrollAction:
                localStorage.setItem(
                    "analysisScrollAction",
                    (!state.core.isScrollActionZoom).toString()
                );
                break;
            case Actions.ActionType.ChangeWaveform:
                action.legacyClient.changeWaveform(
                    state.core.sessionMetadata.sessionId,
                    state.analysisSet.primaryAnalystSetId,
                    state.core.isViewOnly,
                    action.waveformToChangeTo
                );
                break;
            case Actions.ActionType.JumpToTimepoint:
                state.core.peaksJs.player.seekBySeconds(action.timepoint);
                break;
            case Actions.ActionType.DeselectEditingEvents:
                const peaksJs = state.core.peaksJs;
                const segments = peaksJs.segments.getSegments({ editMode: true }) as PeaksPeriod[];
                const points = peaksJs.points.getPoints({ editMode: true }) as PeaksPoint[];

                segments.forEach(s => {
                    const newSegment = { ...s, editMode: false };
                    peaksJs.segments.remove(s);
                    peaksJs.segments.add(newSegment);
                });

                points.forEach(p => {
                    const newPoint = { ...p, editMode: false };
                    peaksJs.points.remove(p);
                    peaksJs.points.add(newPoint);
                });

                break;
            default:
        }
        return next(action);
    };
}

function registerPeaksEvents(dispatch: Dispatch<AnalyseState>, state) {
    state.core.peaksJs.on("player_ended", _data => {
        dispatch(Actions.PauseAsyncAction.create());
    });
}

function finishMovePointEvent(state: AnalyseState, dispatch: Dispatch<AnalyseState>, coughMarshalClient: CoughMarshalClient, legacyClient: WebPortalLegacyClientInternal) {
    const point = state.core.peaksJs.waveform.activePoint;
    const eventType = state.core.availableEventTypes.find(x => x.Id === point.eventTypeId);

    // If there is already a point of the SAME TYPE, then editing the point to arrive at this position is not a valid edit.
    if (state.core.peaksJs.pointOrSegmentExists(point, !eventType.IsSegmented)) {
        toast.error("A Marker Of Type " + eventType.EventName + " Already Exists At This Point.");
        return;
    }

    removeEventFetch(state, point, legacyClient).then(() => {
        point.editMode = false;
        addEventFetch(state, point, coughMarshalClient, legacyClient).then(() => {
            // The edit is now complete, can now switch peaks back to non-edit mode.
            dispatch(Actions.ClosePeaksEditModeAction.create());
        });
    });
}

function finishMovePeriodEvent(state: AnalyseState, dispatch: Dispatch<AnalyseState>, coughMarshalClient: CoughMarshalClient, legacyClient: WebPortalLegacyClientInternal) {
    const period = state.core.peaksJs.waveform.activeSegment;

    return removeEventFetch(state, period, legacyClient)
        .then(() => {
            period.editMode = false;
            return addEventFetch(state, period, coughMarshalClient, legacyClient);
        })
        .then(() => dispatch(Actions.DeselectEditingEventsAction.create()));
}

function addEventFetch(state: AnalyseState, cliEvent: PeaksPoint | PeaksPeriod, coughMarshalClient: CoughMarshalClient, legacyClient: WebPortalLegacyClientInternal) {
    const eventType = state.core.availableEventTypes.find(x => x.Id === cliEvent.eventTypeId);

    if (eventType.IsSegmented) {
        toast.info(
            "Marking " +
            state.core.peaksJs.segments.getSegmentEventTypeLabel(cliEvent as PeaksPeriod) +
            "."
        );
    } else {
        toast.info(
            "Marking " +
            state.core.peaksJs.points.getPointEventTypeLabel(cliEvent as PeaksPoint) +
            "."
        );
    }

    // The eventTypeId is used as the user may have selected another event in the dropdown whiled editing another type of point.
    // It cannot be assumed that when an event is added/removed that the event type is the type currently chosen.
    return handleAddEventFetch(
        cliEvent,
        mapToServerEvent(
            cliEvent,
            state.analysisSet.primaryAnalystSetId,
            cliEvent.eventTypeId,
            state.core.selectedWaveform
        ),
        eventType,
        state,
        coughMarshalClient,
        legacyClient,
    );
}

const handleAddEventFetch = (
    cliEvent: PeaksPoint | PeaksPeriod,
    serverEvent: ServerEvent,
    eventType: StudyCoughEventType,
    state: AnalyseState,
    coughMarshalClient: CoughMarshalClient,
    legacyClient: WebPortalLegacyClientInternal,
) => {
    try {
        const eventMapping = MapPeriod(state.core.positions, serverEvent.RawStart, serverEvent.RawEnd, serverEvent.ProcessedStart, serverEvent.ProcessedEnd, serverEvent.SensorProcessedStart, serverEvent.SensorProcessedEnd);
        return legacyClient.putCoughAnalysisSetEvent({
            AnalysisSetId: serverEvent.AnalysisSetId,
            EventTypeId: serverEvent.EventTypeId,
            RawStart: eventMapping.rawStart,
            RawEnd: eventMapping.rawEnd,
            ProcessedStart: eventMapping.processedStart,
            ProcessedEnd: eventMapping.processedEnd,
            SensorProcessedStart: eventMapping.sensorProcessedStart,
            SensorProcessedEnd: eventMapping.sensorProcessedEnd
        })
            .then(json => {
                cliEvent = {
                    ...cliEvent,
                    id: json.Id
                };
                if (eventType.IsSegmented) {
                    state.core.peaksJs.segments.add(cliEvent as PeaksPeriod);
                    toast.success(
                        state.core.peaksJs.segments.getSegmentEventTypeLabel(cliEvent as PeaksPeriod) +
                        " marked."
                    );
                } else {
                    state.core.rawTimestampPoints.set(json.Id, json.RawStart);
                    state.core.peaksJs.points.add(cliEvent as PeaksPoint);
                    toast.success(
                        state.core.peaksJs.points.getPointEventTypeLabel(cliEvent as PeaksPoint) +
                        " marked."
                    );
                }
            })
            .catch(error => toast.error(`Failed marking ${eventType.EventName}. ${error.Message}`))
    }
    catch (error) {
        toast.error(error.message)
    }
}

function removeEventFetch(state: AnalyseState, event: PeaksPoint | PeaksPeriod, legacyClient: WebPortalLegacyClientInternal) {
    const removeEvent = {
        Id: event.id
    };

    const eventTypes = state.core.availableEventTypes;
    const eventType = eventTypes.find(x => x.Id === event.eventTypeId);

    return legacyClient.deleteCoughEvent(removeEvent)
        .then(response => {
            // Using the remove API in peaks to remove the point/period.
            // Peaks does the work of determining if the event is a point or period.
            state.core.peaksJs.remove(event);
        })
        .catch(error => toast.error(`Failed to remove ${eventType.EventName}.`));
}
